import React, { FC, useRef, useCallback } from 'react'
import { Field, FieldArray, InjectedFormProps, reduxForm, submit } from 'redux-form'
import { Form, Collapse, Row, Col, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { PlusOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { DragDropContext, SensorAPI, useTouchSensor, useMouseSensor } from 'react-beautiful-dnd'

// reducers
import { RootState } from '../../../reducers'

// atoms
import InputField from '../../../atoms/InputField'
import DragAndDropOptions from '../../../atoms/DragAndDropOptions'
import SwitchField from '../../../atoms/SwitchField'

// interfaces
import { IFormItemData, ITabOption } from '../../../types/interfaces'

// utils
import { ELEMENT_ATTRIBUTES, ELEMENT_TYPES, FORM, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT } from '../../../utils/enums'

// assets
import { ReactComponent as ChevronIcon } from '../../../assets/icons/BaseIcons/chevron-down-1-icon.svg'
import { ReactComponent as HeightIcon } from '../../../assets/icons/BaseMiniIcons/height-icon.svg'
import { ReactComponent as WidthIcon } from '../../../assets/icons/BaseMiniIcons/width-icon.svg'
import { ReactComponent as TrashIcon } from '../../../assets/icons/BaseIcons/trash-1-icon.svg'

const { Panel } = Collapse

export type ComponentProps = {
	onRemoveItem: Function
}
export interface Item {
	id: number
	value: string
	label: string
}

export interface ContainerState {
	cards: Item[]
}

type Props = InjectedFormProps<IFormItemData, ComponentProps> &
	ComponentProps & {
		handleSubmit: Function
	}

// render block of options
const RenderOptions = ({ fields }: any) => {
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const sensorApiRef = useRef<SensorAPI>()

	const mySensor = useCallback((api: SensorAPI) => {
		sensorApiRef.current = api
	}, [])

	const disable = useSelector((state: RootState) => state.formTemplate.disable)

	return (
		<DragDropContext
			/* check if drop destination is the same as source destination = only 1 column */
			onDragEnd={async (dropResult) => {
				if (!dropResult.source || !dropResult.destination) {
					return
				}
				/* move option from source index to destination index */
				await fields.move(dropResult.source.index, dropResult.destination.index)
				dispatch(submit(FORM.TAB_BOX_ELEMENT_FORM))
			}}
			sensors={[mySensor, useTouchSensor, useMouseSensor]}
			enableDefaultSensors={false}
		>
			<DragAndDropOptions key={0} data={fields} disabled={disable} formType={FORM.TAB_BOX_ELEMENT_FORM} optionsType={ELEMENT_TYPES.TAB_BOX} />
			<Button
				className={'btn btn--transparent h-25px my-2 flex items-center shadow-none bg-gray-25'}
				icon={<PlusOutlined />}
				onClick={async () => {
					await fields.push({ title: 'New tab' })
					dispatch(submit(FORM.TAB_BOX_ELEMENT_FORM))
				}}
			>
				{t('loc:Add tab')}
			</Button>
		</DragDropContext>
	)
}

const TabBoxElementForm: FC<Props> = (props) => {
	const { handleSubmit, onRemoveItem } = props
	const { t } = useTranslation()

	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const formValues = useSelector((state: RootState) => state.form?.[FORM.TAB_BOX_ELEMENT_FORM]?.values)
	const formItemId = formValues?.i

	return (
		<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
			<Row className={'mx-4 mb-4'} justify={'space-between'}>
				<h3>{t('loc:Tabs')}</h3>
				<TrashIcon className={'trashIcon'} onClick={() => onRemoveItem(formItemId)} />
			</Row>
			<Collapse expandIcon={({ isActive }) => <ChevronIcon rotate={isActive ? 0 : 180} />} expandIconPosition={'end'} ghost>
				<Panel header={t('loc:Tabs')} key='1'>
					<FieldArray name='options' component={RenderOptions} />
					<Row justify={'center'} className={'mt-2 w-full'}>
						<Col className={'flex items-center'} span={21}>
							<p className={'m-0 text-gray-600'}>{t('loc:Operator can switch tabs')}</p>
						</Col>
						<Col span={3}>
							<Field component={SwitchField} name={ELEMENT_ATTRIBUTES.ALLOW_SWITCH_TABS} className={'float-right'} disabled={disable} />
						</Col>
					</Row>
				</Panel>
				<Panel header={t('loc:Size')} key='2'>
					<Row justify={'center'} className={'my-2 w-full'}>
						<Col span={11}>
							<Field
								className={'element-width'}
								component={InputField}
								name={ELEMENT_ATTRIBUTES.COL}
								size={'medium'}
								suffix={<WidthIcon />}
								disabled={disable}
								maxLength={2}
							/>
						</Col>
						<Col span={2} />
						<Col span={11}>
							<Field
								className={'element-height'}
								component={InputField}
								name={ELEMENT_ATTRIBUTES.ROW}
								size={'medium'}
								suffix={<HeightIcon />}
								disabled={disable}
								maxLength={3}
							/>
						</Col>
					</Row>
				</Panel>
			</Collapse>
		</Form>
	)
}

// check validity of options from field array
const validateFormArray = (options: ITabOption[]): boolean => options?.every((option: ITabOption) => option?.title !== '')

const form = reduxForm<IFormItemData, ComponentProps>({
	form: FORM.TAB_BOX_ELEMENT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	onChange: debounce((values, dispatch, props) => {
		if (props.anyTouched && validateFormArray(values?.options)) {
			props.submit()
		}
	}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)
})(TabBoxElementForm)

export default form
